/**
 * Created by hzzhao
 */

import * as constants from '../utils/constants';
import * as mainSelectors from '../main/selectors';

import { getBIAvailability, getDisplayFontListFromFontList } from './logics';
import { getCanvasDraggingArea, getCanvasScale, getCurrentStudioScale, getStudioScaleByKey } from '../canvas/selectors';
import { outlineThicknessList, outlineThicknessOption } from './constants';

import { createSelector } from 'reselect';
import { defaultDigitalTextColor } from './constants';
import { getBatchSelectedTextList } from '../canvas/selectors';
import { getCurrentElement } from '../elements/selectors';
import { getSelectedImprintAreaKeys } from '../imprintarea/selectors';
import { getStudioItemType } from '../main/selectors';
import { studioItemType as studioItemTypeEnum } from '../main/constants';

export const getSelectedTextElement = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentImprintAreaKey, textDict) => {
    if (textDict && textDict[currentImprintAreaKey]) {
      const singleSelectedTextElement = textDict[currentImprintAreaKey].find(
        (text) => text.ui.status === constants.elementStatus.SingleSelected
      );
      const tempSelectedTextElement = textDict[currentImprintAreaKey].filter(
        (text) => text.ui.status === constants.elementStatus.TempSelected
      );
      if (singleSelectedTextElement || tempSelectedTextElement.length === 1) {
        return textDict[currentImprintAreaKey].find(
          (text) =>
            text.ui.status === constants.elementStatus.SingleSelected ||
            text.ui.status === constants.elementStatus.TempSelected
        );
      }
    }
    return null;
  }
);

export const getTextElementIsEditing = (state, imprintAreaKey, guid) => {
  const textElements = state.textDict[imprintAreaKey];
  if (textElements) {
    const textElement = textElements.find((text) => text.ui.guid === guid);
    if (textElement) {
      return textElement.ui.isEditing;
    }
  }

  return false;
};

export const getEditingTextElement = (state) => {
  const singleSelectedTextElement = getSelectedTextElement(state);
  if (!singleSelectedTextElement) {
    return null;
  }

  const imprintAreaKey = mainSelectors.getCurrentImprintAreaKey(state);

  const textElements = state.textDict[imprintAreaKey];
  if (textElements) {
    const textElement = textElements.find((text) => text.ui.guid === singleSelectedTextElement.ui.guid);
    if (textElement && textElement.ui.isEditing) {
      return textElement;
    }
  }

  return null;
};

export const getMaxAvailableFontSize = (state, guid) => {
  const imprintAreaKey = mainSelectors.getCurrentImprintAreaKey(state);
  const textElements = state.textDict[imprintAreaKey];
  if (textElements) {
    const textElement = textElements.find((text) => text.ui.guid === guid);
    if (textElement) {
      return textElement.ui.maxAvailableFontSize;
    }
  }
};

export const getCurrentFontList = createSelector(
  mainSelectors.getCurrentImprintAreaCode,
  (state) => state.fontDict,
  (imprintAreaCode, fontDict) => {
    const fontList = fontDict[imprintAreaCode];
    return fontList;
  }
);

export const getDisplayFontList = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  mainSelectors.getCurrentImprintAreaCode,
  (state) => state.textDict,
  (state) => state.fontDict,
  (currentImprintAreaKey, imprintAreaCode, textDict, fontDict) => {
    let displayFontList = [];
    if (textDict && textDict[currentImprintAreaKey]) {
      const textElementList = textDict[currentImprintAreaKey];
      const selectedTextElement = textElementList.find(
        (element) =>
          element.ui.status === constants.elementStatus.SingleSelected ||
          element.ui.status === constants.elementStatus.TempSelected
      );

      const batchSelectedTextElement = textElementList.filter(
        (element) => element.ui.status === constants.elementStatus.BatchSelected
      );

      if (selectedTextElement) {
        const fontFace = selectedTextElement.data.fontFace;
        const fontList = fontDict[imprintAreaCode];
        displayFontList = getDisplayFontListFromFontList(fontList);
        displayFontList.forEach((item3) => {
          if (item3.fontFace === fontFace) {
            item3.selected = true;
            return false;
          }
          return true;
        });
      } else if (batchSelectedTextElement && batchSelectedTextElement.length > 0) {
        const fontList = fontDict[imprintAreaCode];
        displayFontList = getDisplayFontListFromFontList(fontList);
        return displayFontList;
      }
    }
    return displayFontList;
  }
);

export const getFontSizeList = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  mainSelectors.getCurrentImprintAreaCode,
  (state) => state.textDict,
  (state) => state.fontSizeRangeDict,
  (currentImprintAreaKey, currentImprintAreaCode, textDict, fontSizeRangeDict) => {
    // get fontSizeRange object
    const fontSizeRange = fontSizeRangeDict[currentImprintAreaCode];

    // get selected fontsize
    let selectedFontSize = fontSizeRange.minFontSize;
    if (textDict && textDict[currentImprintAreaKey]) {
      const textElementList = textDict[currentImprintAreaKey];
      const selectedTextElement = textElementList.find(
        (element) => element.ui.status === constants.elementStatus.SingleSelected
      );
      if (selectedTextElement) {
        selectedFontSize = selectedTextElement.data.fontSize;
      }
    }

    // generate fontsize list
    let fontSizeList = [];
    for (let i = fontSizeRange.minFontSize; i <= fontSizeRange.maxFontSize; i++) {
      let selected = false;
      if (i === selectedFontSize) {
        selected = true;
      }
      fontSizeList.push({ value: i, selected });
    }

    return fontSizeList;
  }
);
export const getFontSizeRange = createSelector(
  mainSelectors.getCurrentImprintAreaCode,
  (state) => state.fontSizeRangeDict,
  (currentImprintAreaCode, fontSizeRangeDict) => {
    // get fontSizeRange object
    const fontSizeRange = fontSizeRangeDict[currentImprintAreaCode];
    return fontSizeRange;
  }
);
export const getTextStyleStatus = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  mainSelectors.getStudioItemType,
  getCurrentFontList,
  getCurrentElement,
  (state) => state.textDict,
  (currentImprintAreaKey, currentStudioItemType, fontList, currentElement, textDict) => {
    if (textDict && textDict[currentImprintAreaKey]) {
      const textElementList = textDict[currentImprintAreaKey];
      const selectedTextElement = textElementList.find(
        (element) => element.ui.status === constants.elementStatus.SingleSelected
      );
      if (selectedTextElement) {
        const isBold = selectedTextElement.data.isBold;
        const isItalic = selectedTextElement.data.isItalic;
        const isUnderline = selectedTextElement.data.isUnderline;
        const biAvailability = getBIAvailability(currentElement, fontList);
        return { currentStudioItemType, isBold, isItalic, isUnderline, ...biAvailability };
      }
    }
    return {};
  }
);
export const getCanvasTextDataDict = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state.textDict,
  mainSelectors.getStudioItemType,
  (selectedImprintAreaKeys, textDict, studioItemType) => {
    let textDataDict = {};
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      let textDataList = [];
      let textElementList = textDict[selectedImprintAreaKey];
      if (textElementList !== undefined) {
        for (let textElement of textElementList) {
          if (
            textElement.data.imprintText &&
            textElement.data.imprintText.toUpperCase() !== textElement.data.defaultText.trim().toUpperCase()
          ) {
            let textData = Object.assign({}, textElement.data);
            textData.studioItemType = studioItemType;
            textDataList.push(textData);
          }
        }
        textDataDict[selectedImprintAreaKey] = textDataList;
      }
    }
    return textDataDict;
  }
);
export const getCurrentCanvasTextList = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentImprintAreaKey, textDict) => {
    return textDict[currentImprintAreaKey] || [];
  }
);

//include data that imprintText = ''
export const getAllTextDataList = createSelector(getCurrentCanvasTextList, (textDict) => {
  let textDataList = [];
  textDict.forEach((item) => {
    textDataList.push(item.data);
  });
  return textDataList;
});

// nonDigital text
export const getNonDigitalTextDict = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state.textDict,
  (selectedImprintAreaKeys, textDict) => {
    let nonDigitalTextDict = {};
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      if (textDict[selectedImprintAreaKey] !== undefined) {
        nonDigitalTextDict[selectedImprintAreaKey] = textDict[selectedImprintAreaKey];
      } else {
        nonDigitalTextDict[selectedImprintAreaKey] = [];
      }
    }
    return nonDigitalTextDict;
  }
);
export const getCurrentNonDigitalTextList = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  getNonDigitalTextDict,
  (currentImprintAreaKey, nonDigitalTextDict) => {
    const nonDigitalTextList = nonDigitalTextDict[currentImprintAreaKey];
    return nonDigitalTextList;
  }
);
export const getNonDigitalTextDataDict = createSelector(
  getSelectedImprintAreaKeys,
  getNonDigitalTextDict,
  mainSelectors.getStudioItemType,
  (selectedImprintAreaKeys, nonDigitalTextDict, studioItemType) => {
    let nonDigitalTextDataDict = {};
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      let nonDigitalTextDataList = [];
      if (nonDigitalTextDict[selectedImprintAreaKey] !== undefined) {
        nonDigitalTextDict[selectedImprintAreaKey].forEach((text) => {
          let textData = Object.assign({}, text.data);
          textData.studioItemType = studioItemType;
          nonDigitalTextDataList.push(textData);
        });
        nonDigitalTextDataDict[selectedImprintAreaKey] = nonDigitalTextDataList;
      }
    }
    return nonDigitalTextDataDict;
  }
);
export const getCurrentNonDigitalTextDataList = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  getNonDigitalTextDataDict,
  (currentImprintAreaKey, nonDigitalTextDataDict) => {
    const nonDigitalTextDataList = nonDigitalTextDataDict[currentImprintAreaKey];
    return nonDigitalTextDataList;
  }
);
export const getAllNonDigitalTextDataList = createSelector(
  getSelectedImprintAreaKeys,
  getNonDigitalTextDict,
  (selectedImprintAreaKeys, nonDigitalTextDict) => {
    let nonDigitalTextDataList = [];
    for (let selectedImprintAreaKey of selectedImprintAreaKeys) {
      if (nonDigitalTextDict[selectedImprintAreaKey] !== undefined) {
        nonDigitalTextDict[selectedImprintAreaKey].forEach((text) => {
          nonDigitalTextDataList.push(text.data);
        });
      }
    }
    return nonDigitalTextDataList;
  }
);
export const getTextDataDict = createSelector(
  getCanvasTextDataDict,
  getNonDigitalTextDataDict,
  mainSelectors.getStudioItemType,
  (canvasTextDataDict, nonDigitalTextDataDict, studioItemType) => {
    if (studioItemType === studioItemTypeEnum.digital || studioItemType === studioItemTypeEnum.screenPrintedDigital) {
      return canvasTextDataDict;
    } else {
      return nonDigitalTextDataDict;
    }
  }
);
export const getTextLine = (imprintAreaKey) => {
  return createSelector(
    (state) => state.textLineDict,
    (textLineDict) => {
      let keys = imprintAreaKey.split('-');
      let imprintAreaCode = '';
      if (keys.length === 3) {
        imprintAreaCode = keys[0];
      }
      let selectedTextLineDict = {};
      let textLineList = [];
      if (textLineDict[imprintAreaCode] !== undefined) {
        textLineDict[imprintAreaCode].forEach((text) => {
          let textData = Object.assign({}, text);
          textLineList.push(textData);
        });
        selectedTextLineDict[imprintAreaKey] = textLineList;
      }
      return selectedTextLineDict;
    }
  );
};
export const getImprintedTextDataDict = createSelector(getTextDataDict, (textDataDict) => {
  let imprintedTextDataDict = {};
  const keys = Object.keys(textDataDict);
  for (let key of keys) {
    let textList = textDataDict[key];
    let imprintedTextList = textList.filter(
      (text) => text.imprintText && text.imprintText.toUpperCase() !== text.defaultText.trim().toUpperCase()
    );
    imprintedTextDataDict[key] = imprintedTextList;
  }
  return imprintedTextDataDict;
});
export const getNonDigitalToolbarStatus = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  mainSelectors.getStudioItemType,
  getCurrentFontList,
  getCurrentElement,
  (state) => state.textDict,
  (currentImprintAreaKey, currentStudioItemType, fontList, currentElement, textDict) => {
    if (textDict && textDict[currentImprintAreaKey]) {
      const textElementList = textDict[currentImprintAreaKey];
      const selectedTextElement = textElementList.find(
        (element) => element.ui.status === constants.elementStatus.SingleSelected
      );
      if (selectedTextElement) {
        const isBold = selectedTextElement.data.isBold;
        const isItalic = selectedTextElement.data.isItalic;
        const biAvailability = getBIAvailability(currentElement, fontList);
        return { currentStudioItemType, isBold, isItalic, ...biAvailability };
      }
    }
    return {};
  }
);
export const getNonDigitalScale = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  (state) => state.ui.scaleDict,
  (currentImprintAreaKey, scaleDict) => {
    const scale = scaleDict[currentImprintAreaKey];
    return scale;
  }
);
export const getScaleDict = (state) => state.ui.scaleDict;
export const getHeightFit = createSelector(
  (_, imprintAreaKey) => imprintAreaKey,
  (state) => state.ui.heightFits,
  (imprintAreaKey, heightFits) => {
    // When heightFits equals null, that is, the initialization phase, assign it true
    return heightFits[imprintAreaKey] ? heightFits[imprintAreaKey] : true;
  }
);
export const getSpecialSymbols = createSelector(
  (state) => state.ui,
  (ui) => ui.specialSymbols
);
export const hasInvalidCharacter = createSelector(
  (state) => state.ui,
  (ui) => ui.hasInvalidCharacter
);
export const isFirstShiftTab = createSelector(
  (state) => state.ui,
  (ui) => ui.firstShiftTab
);
export const getTextColorEditingStatus = createSelector(
  (state) => state.ui,
  (ui) => ui.isTextColorEditing
);
export const getAllSelectedTextElements = createSelector(
  mainSelectors.getCurrentImprintAreaKey,
  (state) => state.textDict,
  (currentImprintAreaKey, textDict) => {
    if (textDict && textDict[currentImprintAreaKey]) {
      const textElementList = textDict[currentImprintAreaKey];
      return textElementList.filter(
        (element) =>
          element.ui.status === constants.elementStatus.SingleSelected ||
          element.ui.status === constants.elementStatus.BatchSelected ||
          element.ui.status === constants.elementStatus.TempSelected
      );
    }
    return null;
  }
);
export const showApplyToAllButton = createSelector(
  (state) => state.ui,
  (ui) => ui.showApplyToAllButton
);

export const getTextEditingParams = createSelector(
  getCanvasScale,
  getCurrentStudioScale,
  getSelectedTextElement,
  (canvasScale, studioScale, textElement) => {
    if (textElement && textElement.data) {
      const fontSize = textElement.data.fontSize * canvasScale * studioScale;

      const textSize = {
        width: textElement.data.width * canvasScale,
        height: textElement.data.height * canvasScale,
      };

      const marginLeft = (textElement.data.width / textElement.ui.maxWidth) * textElement.ui.marginLeft * canvasScale;
      const marginTop = (textElement.data.height / textElement.ui.maxHeight) * textElement.ui.marginTop * canvasScale;

      const textValue = textElement.data.imprintText || textElement.data.defaultText;
      const textLines = textValue.split('\n').length;

      const lineHeight = textSize.height / textLines;

      return { size: textSize, fontSize, lineHeight, marginLeft, marginTop };
    }
  }
);

export const getTextEditingInputState = createSelector(getSelectedTextElement, (textElement) => {
  if (textElement && textElement.ui) {
    return textElement.ui.inputState;
  }
});

export const getEditingTextWithSymbols = createSelector(getSelectedTextElement, (textElement) => {
  if (textElement && textElement.ui) {
    return textElement.ui.textWithSymbols;
  }
});

export const getBatchTextOutline = createSelector(
  getBatchSelectedTextList,
  getStudioItemType,
  (batchTextList, currentStudioItemType) => {
    if (
      currentStudioItemType === studioItemTypeEnum.screenPrintedDigital ||
      !batchTextList ||
      batchTextList.length === 0
    ) {
      return null;
    }

    if (currentStudioItemType === studioItemTypeEnum.digital) {
      const sameThickness = batchTextList.every(
        (text) => text.data.outlineThickness === batchTextList[0].data.outlineThickness
      );

      const thickness = sameThickness ? batchTextList[0].data.outlineThickness : outlineThicknessOption.None;

      const sameColor = batchTextList.every(
        (text) => text.data.outlineColor.colorId === batchTextList[0].data.outlineColor.colorId
      );

      const outlineColor = sameColor ? batchTextList[0].data.outlineColor : defaultDigitalTextColor;

      return { thickness, outlineColor };
    }
  }
);

export const getBatchTextColor = createSelector(
  getBatchSelectedTextList,
  getStudioItemType,
  (batchTextList, currentStudioItemType) => {
    if (!batchTextList || batchTextList.length === 0) {
      return null;
    }

    if (currentStudioItemType === studioItemTypeEnum.screenPrintedDigital) {
      return batchTextList[0].data && batchTextList[0].data.color;
    }

    if (currentStudioItemType === studioItemTypeEnum.digital) {
      const sameColor = batchTextList.every(
        (text) => text.data.color.colorReference === batchTextList[0].data.color.colorReference
      );

      return sameColor ? batchTextList[0].data.color : defaultDigitalTextColor;
    }
  }
);

export const getBatchTextColors = createSelector(
  getBatchSelectedTextList,
  getStudioItemType,
  (batchTextList, currentStudioItemType) => {
    if (!batchTextList || batchTextList.length === 0) {
      return null;
    }

    if (currentStudioItemType === studioItemTypeEnum.screenPrintedDigital) {
      return batchTextList[0].data && batchTextList[0].data.color;
    }

    if (currentStudioItemType === studioItemTypeEnum.digital) {
      const colors = [];

      batchTextList.forEach((text) => {
        if (!colors.some((c) => c.colorReference === text.data.color.colorReference)) {
          colors.push(text.data.color);
        }
      });

      return colors;
    }
  }
);

export const getBatchTextFonts = createSelector(getBatchSelectedTextList, (batchTextList) => {
  return [...new Set(batchTextList.map((text) => text.data.fontFace))];
});

export const getDefaultText = createSelector(getCanvasDraggingArea, (draggingArea) => {
  return draggingArea.width > 150 ? 'Enter Text' : 'Text';
});

export const getFontStyleInfo = (state, fontFace) => {
  const fontDict = state.fontDict;
  const imprintAreaCode = mainSelectors.getCurrentImprintAreaCode(state);
  const fontList = fontDict[imprintAreaCode];

  const fonts = fontList.filter((f) => f.fontFace === fontFace);

  const normalizeFontFace = fonts[0].fontFace.replace(/\d+/g, '').replace(/\s+/g, ' ').trim();
  if (fonts && fonts.length > 0) {
    const style = {
      fontFamily: normalizeFontFace,
      fontWeight: fonts[0].hasBold ? 'bold' : 'normal',
      fontStyle: fonts[0].hasItalic ? 'italic' : 'normal',
    };
    return style;
  }
};
